/**
 * Resolve editable "blocks" in the rendered docx-preview DOM for the live hover-and-edit
 * review, and build a navigable outline from them. A block is a body paragraph (`<p>`); its
 * text + surrounding context are captured the same way the template-marking flow captures a
 * selection, so the edit can be located and written back into `word/document.xml` (see
 * applyTextEdit).
 *
 * Block identity is the paragraph's index among ALL `<p>` in the render (`p:<index>`). Edits
 * are text substitutions that never add/remove paragraphs, so this index is stable across
 * re-renders — letting the outline track "edited" blocks and the active selection.
 *
 * Reuses body-model.ts (the shared rendered-DOM ↔ tokenizer text model) so hover/edit stays
 * in parity with how the XML round-trip locates text.
 */

import {
  buildBodyModel,
  isExcludedFromBody,
} from '@/modules/org/components/docx-builder/selection/body-model';

const BLOCK_SELECTOR = 'p';
const CONTEXT_CHARS = 40;

export interface DocxBlockInfo {
  /** Stable id: `p:<index-among-all-paragraphs>`. */
  id: string;
  element: HTMLElement;
  /** Raw visible text of the block (what the editor is seeded with). */
  text: string;
  contextBefore: string;
  contextAfter: string;
}

/** All paragraph elements in document order (unfiltered — indexing anchor for stable ids). */
export function allParagraphs(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(BLOCK_SELECTOR));
}

export function paragraphId(index: number): string {
  return `p:${index}`;
}

/** Whether a paragraph is editable body text (not header/footer/footnote, not empty). */
function isEditableParagraph(block: HTMLElement, root: HTMLElement): boolean {
  if (isExcludedFromBody(block, root)) return false;
  return !!(block.textContent || '').trim();
}

/**
 * Find the editable block element for a hover/click target, or null when the target isn't in
 * editable body text (header/footer/footnote, or an empty paragraph).
 */
export function resolveBlockElement(target: Node, root: HTMLElement): HTMLElement | null {
  const el = target.nodeType === Node.TEXT_NODE ? target.parentElement : (target as HTMLElement);
  if (!el) return null;
  const block = el.closest<HTMLElement>(BLOCK_SELECTOR);
  if (!block || !root.contains(block)) return null;
  if (!isEditableParagraph(block, root)) return null;
  return block;
}

/** Locate a block element by its stable id (`p:<index>`), or null if gone. */
export function blockElementById(root: HTMLElement, id: string): HTMLElement | null {
  const index = Number(id.split(':')[1]);
  if (Number.isNaN(index)) return null;
  return allParagraphs(root)[index] ?? null;
}

/**
 * Describe a block's text + context using the body model (built fresh from the current DOM).
 * Returns null if the block maps to no addressable text.
 */
export function describeBlock(root: HTMLElement, block: HTMLElement): DocxBlockInfo | null {
  const model = buildBodyModel(root);

  // Blocks own a contiguous run of text-node segments in document order; take the first and
  // last segment contained by this block to bound its text within the flattened model.
  let start = -1;
  let end = -1;
  for (const seg of model.nodes) {
    if (block.contains(seg.node)) {
      if (start < 0) start = seg.start;
      end = seg.start + (seg.node.textContent?.length ?? 0);
    }
  }
  if (start < 0 || end <= start) return null;

  const index = allParagraphs(root).indexOf(block);

  return {
    id: paragraphId(index),
    element: block,
    text: model.text.slice(start, end),
    contextBefore: model.text.slice(Math.max(0, start - CONTEXT_CHARS), start),
    contextAfter: model.text.slice(end, end + CONTEXT_CHARS),
  };
}

// ---------------------------------------------------------------------------
// Outline
// ---------------------------------------------------------------------------

export interface OutlineItem {
  id: string;
  /** Truncated preview of the paragraph text. */
  text: string;
}

export interface OutlineSection {
  /** Heading id (`p:<index>`), or null for the lead-in section before the first heading. */
  headingId: string | null;
  title: string;
  /** Heading depth (1-6); 0 for the lead-in section. */
  level: number;
  items: OutlineItem[];
}

/** docx-preview emits Word heading styles as classes like `docxpv_Heading1` / `docxpv_Title`. */
function headingLevel(block: HTMLElement): number | null {
  const cls = typeof block.className === 'string' ? block.className : '';
  if (/(^|[-_\s])title($|[-_\s])/i.test(cls)) return 1;
  const m = /heading\s*([1-6])/i.exec(cls);
  if (m) return Number(m[1]);
  if (/heading/i.test(cls)) return 1;
  return null;
}

/**
 * Build a sectioned outline of the document body: each heading starts a section that collects
 * the body paragraphs following it. Paragraphs before the first heading fall into a lead-in
 * section (title null). Empty/excluded paragraphs are skipped, but ids stay anchored to the
 * full paragraph index so they match {@link describeBlock}.
 */
export function buildOutline(root: HTMLElement): OutlineSection[] {
  const paragraphs = allParagraphs(root);
  const sections: OutlineSection[] = [];
  let current: OutlineSection = { headingId: null, title: '', level: 0, items: [] };

  paragraphs.forEach((p, index) => {
    if (!isEditableParagraph(p, root)) return;
    const text = (p.textContent || '').replace(/\s+/g, ' ').trim();
    const level = headingLevel(p);
    if (level != null) {
      // Close the current section (if it has content) and open a new heading section.
      if (current.items.length || current.headingId != null) sections.push(current);
      current = { headingId: paragraphId(index), title: text, level, items: [] };
    } else {
      current.items.push({ id: paragraphId(index), text });
    }
  });
  if (current.items.length || current.headingId != null) sections.push(current);

  return sections;
}
